import { gameLogger } from '../../utils/GameLogger';
import { ParticleEffectConfig, ParticleParameters, RarityColor, DamageTypeColor } from './types/EffectTypes';
import { ParticleEffects } from './config/ParticleEffects';
import { createParticleEmitter } from '../rendering/utils/ParticleEmitter';
import { createGraphics } from '../rendering/utils/Graphics';
import type { IParticleEmitter, ParticleEmitterConfig, IGraphics } from '../rendering/IRenderer';
import { PixiAssets } from '../assets/PixiAssets';
import type { IAssets } from '../assets/IAssets';

// Phaser ParticleParameters fields can be a constant, a {min,max} range,
// or a {start,end} lerp. These coerce the variants to a single shape.
function toRange(v: unknown, defMin: number, defMax: number): { min: number; max: number } {
  if (typeof v === 'number') return { min: v, max: v };
  if (v && typeof v === 'object') {
    const o = v as { min?: number; max?: number };
    if (typeof o.min === 'number' && typeof o.max === 'number') return { min: o.min, max: o.max };
  }
  return { min: defMin, max: defMax };
}
function toLerp(v: unknown, defStart: number, defEnd: number): { start: number; end: number } {
  if (typeof v === 'number') return { start: v, end: v };
  if (v && typeof v === 'object') {
    const o = v as { start?: number; end?: number; min?: number; max?: number };
    if (typeof o.start === 'number' && typeof o.end === 'number') return { start: o.start, end: o.end };
    if (typeof o.min === 'number' && typeof o.max === 'number') return { start: o.max, end: o.min };
  }
  return { start: defStart, end: defEnd };
}

/**
 * Pure conversion: legacy ParticleEffectConfig → the unified
 * ParticleEmitterConfig (VFX-evolution M1.5). The SINGLE mapping seam —
 * one config vocabulary, no per-call coercion scattered around. Carries
 * the M1 richness the old coercion dropped: `tint` array → color→colorEnd
 * gradient, explicit `colorEnd`, and `glowTexture` → textureKey (bloom).
 * Exported + pure for unit testing. The legacy `texture` asset is NOT used
 * by the Pixi emitter (it draws tinted circles / glow sprites), so it's
 * intentionally absent here.
 */
export function particleConfigToEmitterConfig(config: ParticleEffectConfig): ParticleEmitterConfig {
  const p = config.parameters;
  const speedRange = toRange(p.speed, 0, 100);
  const angleRange = toRange(p.angle, 0, 360);
  const scaleLerp = toLerp(p.scale, 1, 0);
  const alphaLerp = toLerp(p.alpha, 1, 0);

  const tintArr = Array.isArray(p.tint) ? p.tint : undefined;
  const color = typeof p.tint === 'number'
    ? p.tint
    : tintArr && tintArr.length > 0 ? tintArr[0] : 0xffffff;
  const colorEnd = p.colorEnd
    ?? (tintArr && tintArr.length > 1 ? tintArr[tintArr.length - 1] : undefined);

  return {
    mode: config.type === 'emitter' || config.type === 'trail' ? 'continuous' : 'burst',
    color,
    colorEnd,
    textureKey: p.glowTexture,
    speedMin: speedRange.min,
    speedMax: speedRange.max,
    angleMinDeg: angleRange.min,
    angleMaxDeg: angleRange.max,
    gravityY: p.gravityY,
    lifespanMs: typeof p.lifespan === 'number' ? p.lifespan : 1000,
    scaleStart: scaleLerp.start,
    scaleEnd: scaleLerp.end,
    alphaStart: alphaLerp.start,
    alphaEnd: alphaLerp.end,
    quantityPerExplode: p.quantity ?? 1,
    frequencyMs: typeof p.frequency === 'number' && p.frequency > 0 ? p.frequency : undefined,
    blendMode: String(p.blendMode ?? '').toUpperCase() === 'ADD' ? 'add' : 'normal',
    depth: 10,
  };
}

/**
 * Enhanced Particle Manager with texture-based particles and advanced features
 * Maintains backward compatibility with existing ParticleManager API
 */
export class EnhancedParticleManager {
  private static instance: EnhancedParticleManager;
  private scene: unknown;
  private assets: IAssets;

  // Particle emitters by effect ID — substrate-agnostic via IParticleEmitter
  // (PhaserParticleEmitter wraps Phaser.GameObjects.Particles; PixiParticleEmitter
  // is a custom mini-emitter on the Pixi overlay).
  private emitters: Map<string, IParticleEmitter> = new Map();

  // Particle pools for Graphics-based particles (backward compatibility).
  // Phase H — substrate-aware via IGraphics. Each pooled "particle" is an
  // IGraphics handle with a 3px white circle drawn at relative origin;
  // setVisible(false) parks unused particles, setPosition+rAF animates
  // active ones (replaces scene.tweens.add scatter pattern).
  private particlePools: Map<string, IGraphics[]> = new Map();

  // Effect configurations
  private effectConfigs: Map<string, ParticleEffectConfig> = new Map();

  // Default particle texture (fallback if texture not loaded)
  private defaultTexture: string = 'particle_default';

  private constructor(scene: unknown) {
    this.scene = scene;
    this.assets = new PixiAssets();
    // Only create particle pool if scene is fully initialized
    if (scene && (scene as any).add) {
      this.createDefaultParticlePool();
    }
  }

  /**
   * Get singleton instance
   */
  public static getInstance(scene?: unknown): EnhancedParticleManager {
    if (!EnhancedParticleManager.instance && scene) {
      EnhancedParticleManager.instance = new EnhancedParticleManager(scene);
    } else if (EnhancedParticleManager.instance && scene) {
      EnhancedParticleManager.instance.setScene(scene);
    }
    return EnhancedParticleManager.instance;
  }

  /**
   * Set the scene reference
   */
  public setScene(scene: unknown): void {
    if (this.scene !== scene) {
      gameLogger.debug('effects', '✨ [ParticleManager] Scene changed, refreshing emitters...');
      this.cleanup();
      this.scene = scene;
      this.assets = new PixiAssets();

      if (scene && (scene as any).add) {
        this.createDefaultParticlePool();
      }
    }
  }

  /**
   * Initialize the particle system
   */
  public initialize(): void {
    // Phase H10 Arc λ.9 Stage B4 phase 2 — guard relaxed to require only
    // IAssets. Pre-Stage-B4 the scene-plugin check (`scene.textures`,
    // `scene.add`) gated against early-init; with substrate-agnostic
    // factories that check is no longer needed because the factories
    // route through Pixi under default mode (scene arg unused).
    if (!this.assets) {
      gameLogger.warn('effects', '⚠️ EnhancedParticleManager: IAssets not ready, skipping initialization');
      return;
    }

    try {
      this.preloadDefaultTextures();
      this.createDefaultEmitters();
    } catch (error) {
      gameLogger.warn('effects', '⚠️ EnhancedParticleManager: Failed to initialize, some effects may not work', error);
    }
  }

  /**
   * Preload default particle textures
   */
  public preloadDefaultTextures(): void {
    const textures = [
      { key: 'sparkle', color: 0xffffff, type: 'star' },
      { key: 'particle_blue', color: 0x0088ff, type: 'circle' },
      { key: 'hit_spark', color: 0xff6b35, type: 'burst' },
      { key: 'fire_particle', color: 0xff0000, type: 'circle' },
      { key: 'ice_particle', color: 0x4cc9f0, type: 'circle' },
      { key: 'lightning_particle', color: 0xf9c74f, type: 'streak' },
      { key: 'nature_particle', color: 0x44cc44, type: 'circle' },
      { key: 'shadow_particle', color: 0x9d4edd, type: 'circle' },
      { key: 'holy_particle', color: 0xffd700, type: 'star' },
      { key: 'particle_default', color: 0xffffff, type: 'circle' },
    ];

    textures.forEach(({ key, color, type }) => {
      if (!this.assets.hasTexture(key)) {
        this.createPlaceholderTexture(key, color, type);
        gameLogger.debug('effects', `Created placeholder texture: ${key}`);
      }
    });
  }

  /**
   * Create placeholder particle texture via canvas 2D + IAssets.loadImage.
   *
   * Phase H10 Arc λ.9 Stage B2 — migrated off `this.scene.add.graphics()`.
   * Same shapes (star/burst/streak/circle) at 32×32 produced as a data
   * URI through an offscreen canvas, then registered via IAssets so both
   * PhaserAssets and PixiAssets impls can serve the texture.
   */
  private createPlaceholderTexture(key: string, color: number, type: string): void {
    const size = 32;
    let canvas: HTMLCanvasElement;
    try {
      canvas = document.createElement('canvas');
    } catch {
      return; // SSR / no-DOM env — particle textures are optional dev-side
    }
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const fill = `rgba(${r},${g},${b},1)`;

    ctx.fillStyle = fill;
    ctx.strokeStyle = fill;
    ctx.lineWidth = 1;

    switch (type) {
    case 'star': {
      // Star: outer circle in source color, inner circle in yellow
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,0,1)';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 4, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'burst': {
      // 8 triangle pairs radiating from center
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const x1 = size / 2 + Math.cos(angle) * size / 2;
        const y1 = size / 2 + Math.sin(angle) * size / 2;
        const x2 = size / 2 + Math.cos(angle + Math.PI / 8) * size / 4;
        const y2 = size / 2 + Math.sin(angle + Math.PI / 8) * size / 4;
        const x3 = size / 2 + Math.cos(angle - Math.PI / 8) * size / 4;
        const y3 = size / 2 + Math.sin(angle - Math.PI / 8) * size / 4;
        ctx.beginPath();
        ctx.moveTo(size / 2, size / 2);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(size / 2, size / 2);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x3, y3);
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
    case 'streak': {
      // Vertical streak with tapered triangle caps
      ctx.fillRect(size / 4, 0, size / 2, size);
      ctx.fillStyle = `rgba(${r},${g},${b},0.5)`;
      ctx.beginPath();
      ctx.moveTo(size / 4, 0);
      ctx.lineTo(size / 2, size / 4);
      ctx.lineTo(size * 3 / 4, 0);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(size / 4, size);
      ctx.lineTo(size / 2, size * 3 / 4);
      ctx.lineTo(size * 3 / 4, size);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'circle':
    default: {
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    }

    const dataUri = canvas.toDataURL('image/png');
    void this.assets.loadImage(key, dataUri);
  }

  /**
   * Create default particle emitters for common effects
   */
  private createDefaultEmitters(): void {
    // Sparkle emitter (for items)
    this.createEmitter('sparkle', {
      id: 'sparkle',
      type: 'emitter',
      texture: 'sparkle',
      poolSize: 20,
      parameters: {
        speed: { min: 10, max: 30 },
        scale: { start: 0.5, end: 0 },
        lifespan: 1000,
        alpha: { start: 1, end: 0 },
        quantity: 1,
        frequency: 100,
        gravityY: -50,
        angle: { min: 0, max: 360 },
        blendMode: 'ADD'
      }
    });

    // Hit spark emitter (for combat)
    this.createEmitter('hit_spark', {
      id: 'hit_spark',
      type: 'burst',
      texture: 'hit_spark',
      poolSize: 30,
      parameters: {
        speed: { min: 50, max: 150 },
        scale: { start: 1, end: 0 },
        lifespan: 400,
        alpha: { start: 1, end: 0 },
        quantity: 5,
        angle: { min: 0, max: 360 },
        blendMode: 'ADD'
      }
    });

    // Landing dust emitter (backward compatibility)
    this.createEmitter('landing_dust', {
      id: 'landing_dust',
      type: 'burst',
      texture: 'particle_default',
      poolSize: 20,
      parameters: {
        speed: { min: 30, max: 80 },
        scale: { start: 1, end: 0.5 },
        lifespan: 400,
        alpha: { start: 0.8, end: 0 },
        quantity: 3,
        angle: { min: 180, max: 360 },
        gravityY: -20
      }
    });

    // Fire particle emitter (for fire hit effects)
    this.createEmitter('fire_particle', {
      id: 'fire_particle',
      type: 'burst',
      texture: 'fire_particle',
      poolSize: 20,
      parameters: {
        speed: { min: 40, max: 120 },
        scale: { start: 0.8, end: 0 },
        lifespan: 500,
        alpha: { start: 1, end: 0 },
        quantity: 6,
        angle: { min: 0, max: 360 },
        gravityY: -30,
        blendMode: 'ADD'
      }
    });

    // Ice particle emitter (for ice hit effects)
    this.createEmitter('ice_particle', {
      id: 'ice_particle',
      type: 'burst',
      texture: 'ice_particle',
      poolSize: 20,
      parameters: {
        speed: { min: 30, max: 100 },
        scale: { start: 0.7, end: 0 },
        lifespan: 600,
        alpha: { start: 1, end: 0 },
        quantity: 5,
        angle: { min: 0, max: 360 },
        gravityY: -20,
        blendMode: 'ADD'
      }
    });

    // Lightning particle emitter (for lightning hit effects)
    this.createEmitter('lightning_particle', {
      id: 'lightning_particle',
      type: 'burst',
      texture: 'lightning_particle',
      poolSize: 20,
      parameters: {
        speed: { min: 60, max: 180 },
        scale: { start: 0.6, end: 0 },
        lifespan: 300,
        alpha: { start: 1, end: 0 },
        quantity: 4,
        angle: { min: 0, max: 360 },
        blendMode: 'ADD'
      }
    });

    // Smoke emitter
    if (ParticleEffects.smoke) {
      this.createEmitter('smoke', ParticleEffects.smoke);
    }
  }

  /**
   * Create default Graphics-based particle pool (backward compatibility).
   * Phase H — substrate-aware: each pooled handle is an IGraphics with
   * a 3px white circle drawn at relative coords; setPosition + setVisible
   * + rAF mutates them in spawnLegacyLandingDust.
   */
  private createDefaultParticlePool(): void {
    const pool: IGraphics[] = [];
    for (let i = 0; i < 20; i++) {
      const particle = createGraphics(this.scene, { depth: 10 });
      particle.fillCircle(0, 0, 3, 0xFFFFFF, 0.8);
      particle.setVisible(false);
      pool.push(particle);
    }
    this.particlePools.set('default', pool);
  }

  /**
   * Create a new particle emitter from configuration. Substrate-aware
   * via createParticleEmitter — Phaser emitters use the texture cache;
   * Pixi mini-emitters draw solid-color circles via Pixi.Graphics.
   */
  public createEmitter(effectId: string, config: ParticleEffectConfig): IParticleEmitter | null {
    // Phase H10 Arc λ.9 Stage B4 phase 2 — scene-plugin guard removed.
    // The substrate-agnostic createParticleEmitter factory routes through
    // Pixi under default mode (post-λ.7); scene arg is structurally
    // required but unused. IAssets handles texture-existence check.
    if (!this.assets) {
      gameLogger.warn('effects', `Cannot create emitter '${effectId}': IAssets not initialized`);
      return null;
    }

    const texture = this.assets.hasTexture(config.texture) ? config.texture : this.defaultTexture;

    // One conversion seam (M1.5) — carries the unified richness (tint
    // gradient → colorEnd, glowTexture → textureKey/bloom).
    const cfg = particleConfigToEmitterConfig(config);

    const emitter = createParticleEmitter(this.scene, texture, cfg);

    // Store emitter and config
    this.emitters.set(effectId, emitter);
    this.effectConfigs.set(effectId, config);

    return emitter;
  }

  /**
   * Spawn particles at position using effect ID
   */
  public spawnParticles(effectId: string, x: number, y: number, configOverrides?: Partial<ParticleParameters>): void {
    const emitter = this.emitters.get(effectId);
    if (!emitter) {
      gameLogger.warn('effects', `Emitter not found for effect ID: ${effectId}`);
      return;
    }

    // Apply position
    emitter.setPosition(x, y);

    // Config overrides not yet supported through the abstraction. Tracked
    // in the shape of ParticleParameters; would require a "spawn one-off
    // with override" emitter API. None of our 7 emitters use this today.
    void configOverrides;

    // Dispatch by config type. The substrate-aware emitter handles
    // explode (burst) vs start (continuous) identically across Phaser
    // and Pixi paths.
    const config = this.effectConfigs.get(effectId);
    if (config) {
      switch (config.type) {
      case 'burst':
        emitter.explode(config.parameters.quantity || 1, x, y);
        break;
      case 'emitter':
      case 'trail':
        emitter.start();
        if (config.type === 'emitter' && typeof config.parameters.lifespan === 'number') {
          setTimeout(() => emitter.stop(), config.parameters.lifespan);
        }
        break;
      default:
        emitter.explode(1, x, y);
      }
    }
  }

  /**
   * Spawn a particle burst (multiple particles at once)
   */
  public spawnBurst(effectId: string, x: number, y: number, count: number): void {
    const emitter = this.emitters.get(effectId);
    if (emitter) {
      emitter.explode(count, x, y);
    }
  }

  /**
   * Stop a specific emitter
   */
  public stopEmitter(effectId: string): void {
    const emitter = this.emitters.get(effectId);
    if (emitter) {
      emitter.stop();
    }
  }

  /**
   * Stop all emitters
   */
  public stopAllEmitters(): void {
    this.emitters.forEach(emitter => emitter.stop());
  }

  // ===== COMBAT EFFECTS =====

  /**
   * Spawn hit spark effect for combat
   */
  public spawnHitSpark(x: number, y: number, _damageType?: keyof typeof DamageTypeColor): void {
    // Create a temporary emitter with damage type tint
    this.spawnParticles('hit_spark', x, y);

    // Also spawn a smaller burst of colored particles
    this.spawnBurst('sparkle', x, y, 3);
  }

  /**
   * Spawn damage number particles (visual feedback for damage)
   */
  public spawnDamageEffect(_amount: number, x: number, y: number, isCritical: boolean = false): void {
    const effectId = isCritical ? 'hit_spark' : 'sparkle';
    const count = isCritical ? 8 : 3;

    this.spawnBurst(effectId, x, y, count);

    if (isCritical) {
      // Additional screen shake would be handled by VFXSystem
      this.spawnBurst('sparkle', x, y, 5);
    }
  }

  /**
   * Spawn healing effect particles
   */
  public spawnHealingEffect(x: number, y: number, amount: number): void {
    // Green tinted sparkles for healing
    this.spawnBurst('sparkle', x, y, Math.min(Math.floor(amount / 10), 10));
  }

  // ===== ENVIRONMENTAL EFFECTS =====

  /**
   * Spawn landing dust (backward compatibility with existing ParticleManager)
   */
  public spawnLandingDust(x: number, y: number): void {
    // Use new emitter-based system
    this.spawnParticles('landing_dust', x, y);

    // Also maintain backward compatibility with Graphics particles
    this.spawnLegacyLandingDust(x, y);
  }

  /**
   * Legacy landing dust using Graphics particles (backward compatibility).
   * Phase H — substrate-aware: pool of IGraphics handles, rAF stepping
   * x/y/alpha/scale (matches Phaser tween linear ease + duration: 400).
   * IGraphics tracks its own visibility via setVisible — no `.visible`
   * field on the interface, so we use a parallel WeakMap to track
   * which pool slots are in-use during the find-free-slot scan.
   */
  private dustParticleInUse: WeakSet<IGraphics> = new WeakSet();
  private spawnLegacyLandingDust(x: number, y: number): void {
    const pool = this.particlePools.get('default') || [];

    // Spawn 2-3 dust particles
    for (let i = 0; i < 3; i++) {
      const particle = pool.find(p => !this.dustParticleInUse.has(p));
      if (particle) {
        this.dustParticleInUse.add(particle);
        particle.setPosition(x, y);
        particle.setVisible(true);
        particle.setAlpha(1);
        particle.setScale(1);

        const targetX = x + (Math.random() - 0.5) * 60;
        const targetY = y - Math.random() * 20;
        const startTime = performance.now();

        const tick = () => {
          const elapsed = performance.now() - startTime;
          const t = Math.min(elapsed / 400, 1);
          // Linear lerp on position (Phaser default ease was implicit linear)
          particle.setPosition(x + (targetX - x) * t, y + (targetY - y) * t);
          particle.setAlpha(1 - t);
          particle.setScale(1 - 0.5 * t); // 1 → 0.5
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            particle.setVisible(false);
            this.dustParticleInUse.delete(particle);
          }
        };
        requestAnimationFrame(tick);
      }
    }
  }

  /**
   * Spawn water splash effect
   */
  public spawnWaterSplash(x: number, y: number): void {
    // Blue tinted particles for water
    this.spawnBurst('sparkle', x, y, 5);
  }

  /**
   * Spawn smoke effect
   */
  public spawnSmoke(x: number, y: number): void {
    // If smoke emitter exists, use it
    if (this.emitters.has('smoke')) {
      this.spawnParticles('smoke', x, y);
    } else {
      // Fallback to gray tinted particles
      this.spawnBurst('sparkle', x, y, 7);
    }
  }

  // ===== ITEM EFFECTS =====

  /**
   * Spawn item sparkle effect based on rarity
   */
  public spawnItemSparkle(x: number, y: number, _rarity: keyof typeof RarityColor): void {
    // For now, use sparkle emitter
    // In a full implementation, we'd tint the particles
    this.spawnBurst('sparkle', x, y, 5);
  }

  /**
   * Spawn item pickup effect
   */
  public spawnItemPickupEffect(x: number, y: number, _itemName: string): void {
    this.spawnBurst('sparkle', x, y, 8);
  }

  // ===== UTILITY METHODS =====

  /**
   * Clean up all particles and emitters
   */
  public cleanup(): void {
    this.stopAllEmitters();
    this.emitters.forEach(emitter => emitter.destroy());
    this.emitters.clear();

    this.particlePools.forEach(pool => {
      pool.forEach(particle => particle.destroy());
    });
    this.particlePools.clear();

    this.effectConfigs.clear();
  }

  /**
   * Update particle systems (called each frame)
   */
  public update(_delta: number): void {
    // Update any particle logic here
    // Phaser handles most updates automatically
  }
}
